import { useCallback } from 'react';

import { Radio as BaseRadio } from '@base-ui/react/radio';
import { RadioGroup as BaseRadioGroup } from '@base-ui/react/radio-group';
import { IconCheck } from '@tabler/icons-react';
import { useObservability } from 'helpers/observability';

interface PopoverRadioOption<Value extends string> {
  label: string;
  value: Value;
}

interface PopoverRadioOptionsProps<Value extends string> {
  'aria-label': string;
  analyticsName: string;
  onValueChange: (value: Value) => void;
  options: readonly PopoverRadioOption<Value>[];
  value: Value;
}

export const PopoverRadioOptions = <Value extends string>({
  'aria-label': ariaLabel,
  analyticsName,
  onValueChange,
  options,
  value,
}: PopoverRadioOptionsProps<Value>) => {
  const { trackJourneyEvent } = useObservability();

  const handleValueChange = useCallback(
    (nextValue: string) => {
      const option = options.find((candidate) => candidate.value === nextValue);
      if (!option) {
        return;
      }
      trackJourneyEvent('platform', 'radioClickEvent', {
        targetName: analyticsName,
        optionName: option.value,
      });
      onValueChange(option.value);
    },
    [analyticsName, onValueChange, options, trackJourneyEvent],
  );

  return (
    <BaseRadioGroup
      aria-label={ariaLabel}
      value={value}
      onValueChange={handleValueChange}
      className="flex flex-col gap-1"
    >
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <BaseRadio.Root
            key={option.value}
            value={option.value}
            className="flex w-full cursor-pointer items-center justify-between rounded-lg border-0 bg-transparent px-3 py-2 text-left text-body-sm text-od-content-primary hover:bg-od-bg-hover-primary focus-visible:outline-1 focus-visible:outline-offset-1 focus-visible:outline-od-border-focused-secondary data-checked:bg-od-bg-hover-primary"
          >
            {option.label}
            {selected ? <IconCheck size={16} aria-hidden /> : null}
          </BaseRadio.Root>
        );
      })}
    </BaseRadioGroup>
  );
};
